import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { PublicStackParamList } from "@app/navigationApp/types";
import ScreenWrapper from "@app/hoc/ScreenWrapper";
import { ThemedText, ThemedView } from "@app/theme";
import CommonHeader from "@app/sharedComponents/CommonHeader/CommonHeader";
import axios from "axios";
import { endpoints } from "@app/api/apis";
import { useEffect, useState } from "react";
import handleApiErr from "@app/helper/handleApiErr";
import { useDispatch, useSelector } from "react-redux";
import { showErrorSheet } from "@app/store/slices/errorSlice";
import ErrorIcon from "./components/ErrorIcon";
import { Card } from "./components/Card";
import Button from "@app/sharedComponents/Button/Button";
import SecureIcon from "@app/assets/svg/SecureIcon";
import BottomSheetContainer from "@app/sharedComponents/Bottomsheet/Bottomsheet";
import VerificationOTP from "@app/sharedComponents/VerificationOTP/VerificationOTP";
import { addOrUpdateParam, removeQueryFromUrlWithoutReloadV2, setAppHeader } from "@app/utils/utils";
import { QUERY_PARAMS } from "@app/configs/constants";
import { Platform } from "react-native";
import { setShareLinkStepId } from "@app/store/slices/applicationSlice";
import { EndJourney } from "./components/EndJourney";
import { RootState } from "@app/store/newstore";
import { useAppSelector } from "@app/store/hooks";
import ROUTE from "@app/navigationApp/routes";
import LogOutUser, { LogOutUserWithoutResetPartnerParams } from "@app/helper/logout";

const MfdShareLinkInit = ({
    navigation,
    route
}: NativeStackScreenProps<PublicStackParamList, 'MFD_SHARE_LINK_INIT'>) => {

    const dispatch = useDispatch();
    const [isExpired, setIsExpired] = useState<any>(false);
    const [customerDetails, setCustomerDetails] = useState<any>(null);
    const [mfdInitData, setMfdInitData] = useState<any>(null);
    const [btnLoading, setBtnLoading] = useState<any>(false);
    const [showEndJourney, setShowEndJourney] = useState<any>(false);
    const { isMfdShareLink } = useSelector((state: RootState) => state.platformParams);
    const {userDetails} = useAppSelector((state: RootState) => state.auth);
    useEffect(() => {
        if (route?.params?.showEndJourney) {
            setShowEndJourney(true);
        }
    }, [route?.params?.showEndJourney]);

    const onLoad = async () => {
        try {
            const response = await axios.get(endpoints.mfdSharelinkCustomerDetails + isMfdShareLink);
            setCustomerDetails(response?.data);
        } catch (error: any) {
            if (error?.data?.voltErrorCode === "LINK_EXPIRED") {
                setIsExpired(true);
            } else if (handleApiErr(error)) {
                dispatch(showErrorSheet({
                    isErrorSheetVisible: true,
                    errorMessage: error?.data?.message,
                    closeOnBackdropPress: true,
                    errorType: error?.data?.voltErrorCode || error?.data?.statusCode,
                    errorHeading: error?.data?.header,
                    showErrorHeading: true,
                    btnName: 'Ok, got it!',
                }))
            }
        } finally {
            setBtnLoading(false);
        }
    }

    const onInitiate = async () => {
        setBtnLoading(true);
        try {
            const response = await axios.get(endpoints.mfdShareLinkInit + isMfdShareLink);
            setMfdInitData(response?.data);
        } catch (error: any) {
            if (handleApiErr(error)) {
                dispatch(showErrorSheet({
                    isErrorSheetVisible: true,
                    errorMessage: error?.data?.message,
                    closeOnBackdropPress: true,
                    errorType: error?.data?.voltErrorCode || error?.data?.statusCode,
                    errorHeading: error?.data?.header,
                    showErrorHeading: true,
                    btnName: 'Ok, got it!',
                }))
            }
        } finally {
            setBtnLoading(false);
        }
    }

    const onVerificationFailure = (error: any) => {
        if (error?.response?.data?.voltErrorCode === "LINK_EXPIRED" || error?.response?.data?.voltErrorCode === "INVALID_APPLICATION_STEP") {
            setIsExpired(true);
        }
        //CHECK IF OTP LIMIT EXCEEDED IS HANDLED IN INTRINSIC COMPONENT
    }

    const onVerifyOtp = async (data: any) => {
        if(!userDetails && Platform.OS === 'web'){
            const updatedUrl = addOrUpdateParam(window.location.href, QUERY_PARAMS.OC, 'true');
            window.history.pushState({}, '', updatedUrl); 
        }
        await setAppHeader(data?.jwt);
        const stepId = customerDetails?.currentStepId;
        //Consuming it on loader page to check condition and navigate to the application
        dispatch(setShareLinkStepId(stepId));
        //@ts-expect-error
        userDetails && navigation.navigate(ROUTE.KYC_PHOTO_VERIFICATION);
        Platform.OS === 'web' && removeQueryFromUrlWithoutReloadV2(`${[QUERY_PARAMS.SL]}`);
    }

    useEffect(() => {
        onLoad();
        LogOutUserWithoutResetPartnerParams();
    }, []);

    const LinkExpired = () => {
        return (
            <ThemedView backgroundColor="bg-surface-primary" flex={1} padding='xl' justifyContent="center" alignItems="center">
                <ThemedView marginBottom="2xl">
                    <ErrorIcon />
                </ThemedView>
                <ThemedText variant="h5s" color="text-primary" marginBottom='xl'>
                    Link has expired
                </ThemedText>
                <ThemedView padding="md" borderRadius="md" style={{ backgroundColor: '#fff6dd' }} >
                    <ThemedText variant="b3r" color="text-primary">
                        Please request for a new link to complete your application
                    </ThemedText>
                </ThemedView>
            </ThemedView>
        )
    }

    return (
        <ScreenWrapper screenName={route?.name} additionalProperties={{ newArchRoute: true }}>
            <CommonHeader
                label={``}
                navigation={navigation}
                pageName={route.name}
                hideBackButton={true}
            />
            {
                showEndJourney ?
                    <EndJourney
                        navigation={navigation}
                        route={route}
                    />
                    :
                    isExpired ?
                        <LinkExpired />
                        :
                        <ThemedView backgroundColor="bg-surface-primary" flex={1} padding='xl'>
                            <ThemedView>
                                <ThemedText variant="h5s" color="text-primary" marginBottom="sm">
                                    Complete your application for loan against mutual funds
                                </ThemedText>
                            </ThemedView>
                            <ThemedText
                                variant="b4r"
                                color="text-secondary"
                                marginBottom="2xl"
                            >
                                Your funds are only a few steps away!
                            </ThemedText>
                            <Card customerDetails={customerDetails} />
                        </ThemedView>
            }
            {
                !showEndJourney && !isExpired &&
                <ThemedView
                    backgroundColor='bg-surface-primary'
                    borderTopWidth={1}
                    borderTopColor='border-primary'
                >
                    <ThemedView m="lg">
                        <ThemedView flexDirection="row" alignItems="center" gap="sm" justifyContent="center" marginBottom="sm">
                            <SecureIcon />
                            <ThemedText variant="b5s" color="text-success" fontWeight="400">
                                Your data is secure with us
                            </ThemedText>
                        </ThemedView>
                        <Button
                            eventName={route.name + '_GET_MY_PORTFOLIO'}
                            label={'Proceed to selfie verification'}
                            handlePress={() => {
                                !btnLoading && onInitiate();
                            }}
                            type={'SOLID'}
                            size='MD'
                            bg='bg-primary'
                            textColor='text-primary'
                            buttonLoader={btnLoading}
                        />
                    </ThemedView>
                </ThemedView>
            }
            {/* @ts-expect-error */}
            <BottomSheetContainer
                isVisible={mfdInitData}
                close={() => { setMfdInitData(null) }}
                closeOnBackdropPress={true}
                borderTopRightRadius={10}
                borderTopLeftRadius={10}
                containsTextInput
                containerStyle={{ backgroundColor: '#fefefe' }}
            >
                <VerificationOTP
                    heading={`Enter ${mfdInitData?.noOfDigits}-digit OTP`}
                    otpType={"PHONE_NUMBER"}
                    otpSentTo={customerDetails?.maskedPhone}
                    otpDigitCount={6}
                    resendUrl={endpoints?.mfdShareLinkInit + isMfdShareLink}
                    verifyUrl={endpoints?.mfdShareLinkVerify}
                    attributeKeyToVerify={'shortUrl'}
                    attributeValueToVerify={isMfdShareLink}
                    onClose={() => { setMfdInitData(null) }}
                    onVerificationSuccess={onVerifyOtp}
                    onVerificationFailure={onVerificationFailure}
                    resendApiMethod={"GET"}
                />
            </BottomSheetContainer>
        </ScreenWrapper>
    )
}

export default MfdShareLinkInit;